/**
 * Updates free-shipping progress UI from /cart.js (aligned with GWP eligible subtotal rules).
 */
(function () {
  if (typeof document === 'undefined') return;

  var g = /** @type {Record<string, unknown>} */ (/** @type {unknown} */ (window));
  if (g['__freeShippingProgressBound']) return;
  g['__freeShippingProgressBound'] = true;

  /**
   * @param {{
   *   items?: unknown[];
   *   item_count?: number;
   * } | null | undefined} cart
   * @returns {number}
   */
  function eligibleSubtotalCents(cart) {
    /** @type {unknown[]} */
    var items = cart && Array.isArray(cart.items) ? cart.items : [];
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      /** @type {{ properties?: Record<string, string | undefined>; requires_shipping?: boolean; line_price?: number }} */
      var it = items[i] || {};
      var props = it.properties || {};
      if (props._is_gwp === 'true') continue;
      if (props.__lion_sfp_id) continue;
      var requiresShipping = it.requires_shipping !== false;
      if (!requiresShipping) continue;
      var lp = Number(it.line_price || 0);
      if (!Number.isNaN(lp)) total += lp;
    }
    return total;
  }

  /**
   * @param {number} cents
   * @param {string} format
   * @returns {string}
   */
  function formatCents(cents, format) {
    try {
      /** @type {{ formatMoney?: (cents: number, format: string) => string } | undefined} */
      var shopifyMoney =
        typeof Shopify !== 'undefined'
          ? /** @type {{ formatMoney?: (cents: number, format: string) => string }} */ (Shopify)
          : undefined;
      if (shopifyMoney && typeof shopifyMoney.formatMoney === 'function') {
        return shopifyMoney.formatMoney(cents, format);
      }
    } catch (_) {}
    return (cents / 100).toFixed(2);
  }

  /**
   * @param {HTMLElement} root
   * @param {{
   *   items?: unknown[];
   *   item_count?: number;
   * } | null | undefined} cart
   */
  function updateRoot(root, cart) {
    var threshold = parseInt(root.getAttribute('data-threshold-cents') || '0', 10);
    if (Number.isNaN(threshold)) threshold = 0;

    var eligible = eligibleSubtotalCents(cart);
    var useCurrency = root.getAttribute('data-currency-code-enabled') === 'true';
    var moneyFormat = useCurrency
      ? root.getAttribute('data-money-with-currency-format') || root.getAttribute('data-money-format') || ''
      : root.getAttribute('data-money-format') || '';

    var awayTemplate = root.getAttribute('data-away-template') || '';
    var successMessage = root.getAttribute('data-success-message') || '';

    var unlocked = threshold <= 0 || eligible >= threshold;
    var remaining = Math.max(0, threshold - eligible);

    var progressPct = 0;
    if (threshold > 0) {
      progressPct = Math.min(100, Math.round((eligible * 100) / threshold));
    } else if (cart && (cart.item_count ?? 0) > 0) {
      progressPct = 100;
    }

    var msgEl = root.querySelector('[data-fs-message]');
    var fillEl = root.querySelector('[data-fs-fill]');
    var trackEl = root.querySelector('[data-fs-track]');

    if (msgEl) {
      msgEl.textContent = unlocked
        ? successMessage
        : awayTemplate.split('%%AMOUNT%%').join(formatCents(remaining, moneyFormat));
    }

    if (fillEl instanceof HTMLElement) {
      fillEl.style.width = progressPct + '%';
    }

    if (trackEl) {
      trackEl.setAttribute('aria-valuenow', String(progressPct));
    }

    root.setAttribute('data-eligible-cents', String(eligible));
    root.setAttribute('data-unlocked', unlocked ? 'true' : 'false');
    root.classList.toggle('is-complete', unlocked);
  }

  /**
   * @param {CustomEvent} ev
   */
  function onCartUpdate(ev) {
    var d =
      /** @type {{ data?: { didError?: boolean }; resource?: { items?: unknown[]; item_count?: number } } | undefined} */ (
        ev && ev.detail
      );
    if (d && d.data && d.data.didError) return;

    var resource = d && d.resource;
    /** @type {{ items?: unknown[]; item_count?: number } | null} */
    var cart =
      resource && typeof resource === 'object' && Array.isArray(resource.items) ? resource : null;

    var roots = document.querySelectorAll('[data-free-shipping-progress]');
    if (!roots.length) return;

    if (cart) {
      roots.forEach(function (r) {
        if (r instanceof HTMLElement) updateRoot(r, cart);
      });
      return;
    }

    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (/** @type {{ items?: unknown[]; item_count?: number }} */ c) {
        roots.forEach(function (r) {
          if (r instanceof HTMLElement) updateRoot(r, c);
        });
      })
      .catch(function () {});
  }

  document.addEventListener('cart:update', onCartUpdate);

  const MOBILE_CART_MQL = window.matchMedia('(max-width: 749px)');

  /**
   * Returns the preferred mount node for the free shipping progress UI.
   * Prefers the Shopify block wrapper when available.
   *
   * @param {HTMLElement} page
   * @returns {HTMLElement|null}
   */
  const findProgressMount = (page) => {
    const progress = page.querySelector('[data-free-shipping-progress]');

    if (!(progress instanceof HTMLElement)) {
      return null;
    }

    const block = progress.closest('.free-shipping-progress-block');
    return block instanceof HTMLElement ? block : progress;
  };

  /**
   * @param {HTMLElement} target
   * @param {HTMLElement} element
   */
  const moveAfter = (target, element) => {
    if (target.nextElementSibling !== element) {
      target.insertAdjacentElement('afterend', element);
    }
  };

  /**
   * @param {HTMLElement} parent
   * @param {HTMLElement} element
   */
  const moveToTop = (parent, element) => {
    if (
      element.parentElement !== parent ||
      parent.firstElementChild !== element
    ) {
      parent.prepend(element);
    }
  };

  const syncMobileCartBannerPlacement = () => {
    const pages = document.querySelectorAll(
      '.cart-page:not(.cart-page--empty)'
    );

    if (!pages.length) {
      return;
    }

    const isMobile = MOBILE_CART_MQL.matches;

    pages.forEach((page) => {
      if (!(page instanceof HTMLElement)) {
        return;
      }

      const mount = findProgressMount(page);
      const title = page.querySelector('.cart-page__title');
      const summary = page.querySelector('.cart-page__summary');

      if (
        !mount ||
        !(title instanceof HTMLElement) ||
        !(summary instanceof HTMLElement)
      ) {
        return;
      }

      if (isMobile) {
        moveAfter(title, mount);
        page.classList.add('cart-page--fs-elevated');
        return;
      }

      moveToTop(summary, mount);
      page.classList.remove('cart-page--fs-elevated');
    });
  };

  let scheduled = false;

  const scheduleMobileCartBannerPlacement = () => {
    if (scheduled) {
      return;
    }

    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      syncMobileCartBannerPlacement();
    });
  };

  MOBILE_CART_MQL.addEventListener(
    'change',
    scheduleMobileCartBannerPlacement
  );

  document.addEventListener(
    'cart:update',
    scheduleMobileCartBannerPlacement
  );

  document.addEventListener(
    'shopify:section:load',
    scheduleMobileCartBannerPlacement
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      scheduleMobileCartBannerPlacement
    );
  } else {
    scheduleMobileCartBannerPlacement();
  }

  window.addEventListener(
    'load',
    scheduleMobileCartBannerPlacement
  );
})();
